import { useState, useEffect } from 'react';
import copy from 'copy-to-clipboard';
import {
  useLocation,
  useParams,
  useNavigate,
  NavLink,
  generatePath,
} from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@store';
import {
  SidePanel,
  SidePanelType,
  PermissionEnum,
  PermissionDenied,
  SidePanelLoader,
  ButtonPrimary,
  ButtonPseudo,
  IconCopyTwo,
  formatDate,
  IconClock,
  IconArrowRight,
  IconSelected,
  IconLoader,
  BaseTransaction,
  TransactionTypeEnum,
} from '@ui/src';
import { clearIntegrationLog } from './store/slice';
import {
  useLazyGetIntegrationsLogsByIdQuery,
  useLazyGetIntegrationLogTransactionByIdQuery,
} from './store/service';
import { useLazyGetConfigurationDictionariesTransactionTypesQuery } from '../../store/dictionaries/service';

import { BankRoutes } from '../../routes';

import cn from './integration-logs.module.css';

const getStatusCodeDescription = (status: number) => {
  let statusDescription = '';

  switch (status) {
    case 200:
      statusDescription = 'OK';
      break;
    case 201:
      statusDescription = 'Created';
      break;
    case 301:
      statusDescription = 'Moved Permanently';
      break;
    case 302:
      statusDescription = 'Found';
      break;
    case 304:
      statusDescription = 'Not Modified';
      break;
    case 400:
      statusDescription = 'Bad Request';
      break;
    case 401:
      statusDescription = 'Unauthorized';
      break;
    case 403:
      statusDescription = 'Forbidden';
      break;
    case 404:
      statusDescription = 'Not Found';
      break;
    case 429:
      statusDescription = 'Too Many Requests';
      break;
    case 500:
      statusDescription = 'Internal Server Error';
      break;
    case 502:
      statusDescription = 'Bad Gateway';
      break;
    case 503:
      statusDescription = 'Service Unavailable';
      break;
    case 504:
      statusDescription = 'Gateway Timeout';
      break;
    default:
      break;
  }

  return statusDescription;
};

const IntegrationLog = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);

  const {
    configurationDictionaries: { activePermissions, transactionTypes },
    integrationLogs: { log },
  } = useAppSelector((state) => state);

  const { logId } = useParams();
  const { search } = useLocation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [getLogById, { isLoading, isFetching }] =
    useLazyGetIntegrationsLogsByIdQuery();
  const [
    getTransactionById,
    { isLoading: transactionIsLoading, isFetching: transactionIsFetching },
  ] = useLazyGetIntegrationLogTransactionByIdQuery();
  const [getTransactionTypes] =
    useLazyGetConfigurationDictionariesTransactionTypesQuery();

  const handleClose = () => {
    dispatch(clearIntegrationLog());
    setPanelIsOpen(false);
    setCopyIsSuccess(false);

    setTimeout(() => {
      navigate(`${BankRoutes.INTEGRATIONS_LOGS_LIST}${search || ''}`);
    }, 100);
  };

  const onGetTransactionById = async (id: string) => {
    const response = (await getTransactionById(id)) as {
      data: { data: BaseTransaction };
    };

    if (response?.data?.data) {
      const currentTransactionType = transactionTypes?.find(
        (item) => item.id === response?.data?.data?.typeId,
      );

      let link = '';

      if (currentTransactionType?.name === TransactionTypeEnum.INCOMING) {
        link = `/transactions/incoming/${id}`;
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.INTERNAL
      ) {
        link = `/transactions/internal/${id}`;
      } else if (currentTransactionType?.name === TransactionTypeEnum.NOSTRO) {
        link = `/transactions/nostro/${id}`;
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.OUTGOING
      ) {
        link = `/transactions/outgoing/${id}`;
      }

      window.open(link, '_blank');
    }
  };

  const onCopy = () => {
    setCopyIsSuccess(true);

    copy(`
Type: ${
      log?.types
        ? `${log?.types?.charAt(0)}${log?.types?.slice(1).toLowerCase()}`
        : '-'
    }      
Request time: ${
      log?.requestTime ? formatDate(log?.requestTime, true, true) : '-'
    }
Response time: ${
      log?.responseTime ? formatDate(log?.responseTime, true, true) : '-'
    }
Status: ${
      log?.statusCode
        ? `${log?.statusCode} ${getStatusCodeDescription(log?.statusCode)}`
        : '-'
    }  
Bank: ${log?.pspPartner ? log?.pspPartner?.companyName : '-'}    
Method: ${log?.eventType || '-'}
URL: ${log?.url || '-'}
Latency: ${
      log?.requestTime && log?.responseTime
        ? `${
            new Date(log?.responseTime).getTime() -
            new Date(log?.requestTime).getTime()
          } ms`
        : '-'
    }
Integration type: ${log?.connectionType || '-'}
Transaction ID: ${log?.transactionId || '-'}
${
  log?.responseBody
    ? JSON.stringify(
        log?.responseBody.includes('{') && log?.responseBody.charAt(0) === '{'
          ? JSON.parse(log?.responseBody)
          : log?.responseBody,
        null,
        2,
      )
    : ''
}
      `);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (logId) {
      getLogById(logId);
    }
  }, [logId]);

  useEffect(() => {
    if (!transactionTypes) {
      getTransactionTypes();
    }
  }, [transactionTypes]);

  let content = <div />;

  const getTransactionIsAvailable = Boolean(
    activePermissions?.includes(PermissionEnum.TRANSACTIONS_NOSTRO_LIST) ||
    activePermissions?.includes(PermissionEnum.TRANSACTIONS_INCOMING_LIST) ||
    activePermissions?.includes(PermissionEnum.TRANSACTIONS_INTERNAL_LIST) ||
    activePermissions?.includes(PermissionEnum.TRANSACTIONS_OUTGOING_LIST),
  );

  if (!activePermissions?.includes(PermissionEnum.PSP_INTEGRATION_LOGS_LIST)) {
    content = <PermissionDenied />;
  } else if (
    activePermissions?.includes(PermissionEnum.PSP_INTEGRATION_LOGS_LIST)
  ) {
    if (isLoading || isFetching) {
      content = <SidePanelLoader />;
    } else {
      let responseTimeClassName = '';
      let statusCodeClassName = '';

      if (log?.requestTime && log?.responseTime) {
        const difference =
          new Date(log?.responseTime).getTime() -
          new Date(log.requestTime).getTime();

        if (difference >= 1500 && difference <= 2500) {
          responseTimeClassName = cn.yellowIcon;
        } else if (difference > 2500) {
          responseTimeClassName = cn.redIcon;
        }
      } else if (!log?.responseTime) {
        responseTimeClassName = cn.redIcon;
      }

      if (log?.statusCode) {
        if (log?.statusCode?.toString()?.charAt(0) === '2') {
          statusCodeClassName = cn.successStatus;
        } else if (log?.statusCode?.toString()?.charAt(0) === '3') {
          statusCodeClassName = cn.redirectStatus;
        } else if (log?.statusCode?.toString()?.charAt(0) === '4') {
          statusCodeClassName = cn.errorStatusCode;
        } else if (log?.statusCode?.toString()?.charAt(0) === '5') {
          statusCodeClassName = cn.internalErrorStatusCode;
        }
      }

      content = (
        <div className={cn.integrationLogContent}>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Bank</div>
            <div className={cn.integrationLogRowValue}>
              {log?.pspPartnerId ? (
                <NavLink
                  to={generatePath(BankRoutes.BANK_AND_PSP_PAGE, {
                    bankId: log?.pspPartnerId,
                  })}
                  target="_blank"
                  className={cn.bankLink}
                >
                  {log?.pspPartner?.companyName}
                  <IconArrowRight width={14} height={11} />
                </NavLink>
              ) : (
                '-'
              )}
            </div>
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Method</div>
            {log?.eventType ? (
              <div className={cn.integrationLogRowValue}>
                <div className={cn.eventTypeWrapper}>
                  <div className={cn.eventType}>{log.eventType}</div>
                </div>
              </div>
            ) : (
              '-'
            )}
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Type</div>
            <div className={cn.integrationLogRowValue}>
              {log?.types
                ? `${log?.types.charAt(0)}${log?.types.slice(1).toLowerCase()}`
                : '-'}
            </div>
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>URL</div>
            <div className={cn.integrationLogRowValue}>{log?.url || '-'}</div>
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Request Time</div>
            <div className={cn.integrationLogRowValue}>
              {log?.requestTime
                ? formatDate(log?.requestTime, true, true)
                : '-'}
            </div>
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Response Time</div>
            <div className={cn.integrationLogRowValue}>
              {log?.responseTime
                ? formatDate(log?.responseTime, true, true)
                : '-'}
            </div>
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Latency</div>
            <div className={cn.integrationLogRowValue}>
              <div className={cn.integrationLogLatency}>
                <IconClock
                  width={16}
                  height={16}
                  className={responseTimeClassName}
                />
                {log?.responseTime && log?.requestTime
                  ? `${
                      new Date(log?.responseTime).getTime() -
                      new Date(log?.requestTime).getTime()
                    } ms`
                  : '-'}
              </div>
            </div>
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Status</div>
            <div className={cn.integrationLogRowValue}>
              <div className={cn.integrationLogStatusWrapper}>
                <div
                  className={`${cn.integrationLogStatus} ${statusCodeClassName}`}
                >
                  {log?.statusCode}
                </div>
                <div className={cn.integrationLogStatusDescriotion}>
                  {getStatusCodeDescription(log?.statusCode as number)}
                </div>
              </div>
            </div>
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Integration type</div>
            <div className={cn.integrationLogRowValue}>
              {log?.connectionType || '-'}
            </div>
          </div>
          <div className={cn.integrationLogRow}>
            <div className={cn.integrationLogRowTitle}>Transaction ID</div>
            <div className={cn.integrationLogRowValue}>
              <button
                type="button"
                className={`${cn.transactionIdLink} ${getTransactionIsAvailable ? cn.transactionIdLinkActive : ''}`}
                onClick={() => {
                  if (
                    getTransactionIsAvailable &&
                    log?.transactionId &&
                    transactionTypes
                  ) {
                    onGetTransactionById(log?.transactionId);
                  }
                }}
              >
                {log?.transactionId || '-'}
                {getTransactionIsAvailable &&
                  log?.transactionId &&
                  transactionTypes && (
                    <>
                      {transactionIsLoading || transactionIsFetching ? (
                        <IconLoader width={14} height={14} />
                      ) : (
                        <IconArrowRight width={14} height={11} />
                      )}
                    </>
                  )}
              </button>
            </div>
          </div>
          <div className={cn.integrationLogResponseBody}>
            <div className={cn.integrationLogResponseBodyTitle}>Body</div>
            {log?.responseBody && (
              <div className={cn.integrationLogResponseBodyContent}>
                <pre>
                  {log?.responseBody.includes('{') &&
                  log?.responseBody.charAt(0) === '{' ? (
                    JSON.stringify(
                      log?.responseBody.includes('{') &&
                        log?.responseBody.charAt(0) === '{'
                        ? JSON.parse(log?.responseBody)
                        : log?.responseBody,
                      null,
                      2,
                    )
                  ) : (
                    <span className={cn.notFormattedBody}>
                      {log?.responseBody}
                    </span>
                  )}
                </pre>
              </div>
            )}
            {!log?.responseBody && (
              <div className={cn.integrationLogResponseBodyEmpty}>
                No response body yet
              </div>
            )}
          </div>
        </div>
      );
    }
  }

  return (
    <>
      <SidePanel
        isOpen={panelIsOpen}
        type={SidePanelType.Accounts}
        onClose={handleClose}
        title="Integration Log"
        sidepanelExpandedKey="internal-log-panel"
        footer={
          <div className={cn.integrationLogControls}>
            <ButtonPrimary
              text="Copy full log"
              icon={<IconCopyTwo />}
              className={cn.copyLogButton}
              onClick={onCopy}
              disabled={isLoading || isFetching}
              bold
            />
            <ButtonPseudo text="Close" onClick={handleClose} bold />
          </div>
        }
        closeOnClickOutside
      >
        {content}
      </SidePanel>
      {copyIsSuccess && (
        <div className={cn.copySuccessHint}>
          <IconSelected width={18} height={18} /> Log copied
        </div>
      )}
    </>
  );
};

export default IntegrationLog;
